import { parseJsonObject, readObject, readString } from './json';

/**
 * The `presence` topic's payloads - both directions, matching the hub
 * conventions, the assist console's parsers
 * (`assist-console/src/protocol/messages.ts`) and the Unity client
 * (`AssistProtocol.cs` `PresenceMessages`) EXACTLY:
 *
 *   assistant -> { "type":"presence", "id", "name", "role":"assistant", "observer", "av":{ "camera", "mic" } }
 *   wearer    -> { "type":"wearerState", "assist":"live|paused", "assistants":{ "<identity>":{ "hidden", "muted" } } }
 */

/** An assistant's declared presence - its side of the `presence` topic (Plan-03 §2.4). */
export interface AssistantPresence {
  /** The assistant's room identity (`assistant:<slug>#<rand4>`). */
  readonly id: string;
  /** Display name the assistant joined with. */
  readonly name: string;
  /** Observer mode: present without publishing AV (D11 - still listed, RA-C5). */
  readonly observer: boolean;
  /** The assistant's declared camera intent. */
  readonly cameraOn: boolean;
  /** The assistant's declared microphone intent. */
  readonly micOn: boolean;
}

/** One assistant's wearer-side hint inside the wearer-state broadcast (D5). */
export interface WearerAssistantHint {
  readonly identity: string;
  readonly hidden: boolean;
  readonly muted: boolean;
}

/** Wearer assist-state token while assistance is active. */
export const ASSIST_LIVE = 'live';
/** Wearer assist-state token while the master pause holds. */
export const ASSIST_PAUSED = 'paused';

export type WearerAssistState = typeof ASSIST_LIVE | typeof ASSIST_PAUSED;

/**
 * Build the headset's `wearerState` broadcast (Plan-01 §2.2). `assist` must be
 * `live` or `paused` - the wire allows nothing else, so an inactive session
 * simply broadcasts nothing.
 */
export function wearerStateJson(
  assist: WearerAssistState,
  assistants: readonly WearerAssistantHint[],
): string {
  const hints: Record<string, { hidden: boolean; muted: boolean }> = {};
  for (const hint of assistants) {
    if (!hint.identity) continue;
    hints[hint.identity] = { hidden: hint.hidden, muted: hint.muted };
  }
  return JSON.stringify({ type: 'wearerState', assist, assistants: hints });
}

/**
 * Parse an assistant's `presence` message. Null for anything else on the topic
 * (the wearer's own broadcast included) - same narrowing the console applies:
 * `type=="presence"`, `role=="assistant"`, string `id`/`name`, strict booleans
 * elsewhere.
 */
export function parseAssistantPresence(json: string): AssistantPresence | null {
  const root = parseJsonObject(json);
  if (!root) return null;
  if (readString(root, 'type') !== 'presence' || readString(root, 'role') !== 'assistant') return null;

  const id = typeof root['id'] === 'string' ? (root['id'] as string) : null;
  const name = typeof root['name'] === 'string' ? (root['name'] as string) : null;
  if (id === null || name === null) return null;

  const av = readObject(root, 'av');
  return {
    id,
    name,
    observer: root['observer'] === true,
    cameraOn: av?.['camera'] === true,
    micOn: av?.['mic'] === true,
  };
}
